/**
 * flagOptions.js - Lista canonica de FLAGS con sus render functions.
 * Fuente de verdad unica usada por FlagFilterModal, BulkFlagSelectorModal
 * y el dropdown individual en ContactsTable.
 *
 * Cada entrada tiene:
 *   key:    string  identificador unico (coincide con BD campo bandera)
 *   label:  string  nombre legible
 *   emoji:  string|null  emoji raw (null para los que usan SVG)
 *   render: (size?: 'sm'|'md'|'lg') => ReactNode
 */
import React from 'react'

// SVG paths para WhatsApp/Wassenger (misma forma, distinto color)
const WA_PATHS = (
  <>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
    <path d="M12 2C6.486 2 2 6.486 2 12c0 1.86.507 3.6 1.389 5.09L2.07 21.78a.5.5 0 00.612.638l4.857-1.3A9.95 9.95 0 0012 22c5.514 0 10-4.486 10-10S17.514 2 12 2z"/>
  </>
)

// render de "cotizacion enviada": papiro + check verde en esquina
const renderCotizacionEnviada = (size = 'md') => {
  const emojiSize = size === 'sm' ? '1.25rem' : size === 'lg' ? '1.875rem' : '1.5rem'
  const badgeSize = size === 'sm' ? '0.65rem' : size === 'lg' ? '0.95rem' : '0.8rem'
  const offset   = size === 'sm' ? '-2px' : '-4px'
  return (
    <span
      className="relative inline-flex items-center justify-center"
      style={{ fontSize: emojiSize, lineHeight: 1, width: emojiSize, height: emojiSize }}
    >
      <span>{String.fromCodePoint(0x1F4DC)}</span>
      <span style={{ position: 'absolute', bottom: offset, right: offset, fontSize: badgeSize, lineHeight: 1, filter: 'drop-shadow(0 0 1px rgba(255,255,255,0.9))' }}>
        {String.fromCodePoint(0x2705)}
      </span>
    </span>
  )
}

export const FLAG_OPTIONS = [
  {
    key: 'bandera',
    label: 'Bandera',
    emoji: String.fromCodePoint(0x1F6A9),
    render: (size = 'md') => {
      const sz = size === 'sm' ? '1.25rem' : size === 'lg' ? '1.875rem' : '1.5rem'
      return <span style={{ fontSize: sz }}>{String.fromCodePoint(0x1F6A9)}</span>
    }
  },
  {
    key: 'llamar',
    label: 'Llamar',
    emoji: String.fromCodePoint(0x1F4DE),
    render: (size = 'md') => {
      const sz = size === 'sm' ? '1.25rem' : size === 'lg' ? '1.875rem' : '1.5rem'
      return <span style={{ fontSize: sz }}>{String.fromCodePoint(0x1F4DE)}</span>
    }
  },
  {
    key: 'whatsapp',
    label: 'Whatsapp',
    emoji: null,
    render: (size = 'md') => {
      const svgSz = size === 'sm' ? 'w-5 h-5' : size === 'lg' ? 'w-9 h-9' : 'w-7 h-7'
      return (
        <svg className={`${svgSz} text-green-500`} viewBox="0 0 24 24" fill="currentColor">
          {WA_PATHS}
        </svg>
      )
    }
  },
  {
    key: 'wassenger',
    label: 'Wassenger',
    emoji: null,
    render: (size = 'md') => {
      const svgSz = size === 'sm' ? 'w-5 h-5' : size === 'lg' ? 'w-9 h-9' : 'w-7 h-7'
      return (
        <svg className={`${svgSz} text-blue-500`} viewBox="0 0 24 24" fill="currentColor">
          {WA_PATHS}
        </svg>
      )
    }
  },
  {
    key: 'cotizacion',
    label: 'Cotización',
    emoji: String.fromCodePoint(0x1F4DC),
    render: (size = 'md') => {
      const sz = size === 'sm' ? '1.25rem' : size === 'lg' ? '1.875rem' : '1.5rem'
      return <span style={{ fontSize: sz }}>{String.fromCodePoint(0x1F4DC)}</span>
    }
  },
  {
    key: 'cotizacion_enviada',
    label: 'Cotización enviada',
    emoji: null,
    render: (size = 'md') => renderCotizacionEnviada(size)
  },
  {
    key: 'valor',
    label: 'Valor',
    emoji: String.fromCodePoint(0x1F4B0),
    render: (size = 'md') => {
      const sz = size === 'sm' ? '1.25rem' : size === 'lg' ? '1.875rem' : '1.5rem'
      return <span style={{ fontSize: sz }}>{String.fromCodePoint(0x1F4B0)}</span>
    }
  },
  {
    key: 'pregunta',
    label: 'Pendiente',
    emoji: String.fromCodePoint(0x2753),
    render: (size = 'md') => {
      const sz = size === 'sm' ? '1.25rem' : size === 'lg' ? '1.875rem' : '1.5rem'
      return <span style={{ fontSize: sz }}>{String.fromCodePoint(0x2753)}</span>
    }
  },
  {
    key: 'rescatado',
    label: 'Rescatado',
    emoji: String.fromCodePoint(0x267B, 0xFE0F),
    render: (size = 'md') => {
      const sz = size === 'sm' ? '1.25rem' : size === 'lg' ? '1.875rem' : '1.5rem'
      return <span style={{ fontSize: sz }}>{String.fromCodePoint(0x267B, 0xFE0F)}</span>
    }
  },
  {
    key: 'reciclado_hoy',
    label: 'Reciclado para hoy',
    emoji: String.fromCodePoint(0x267B, 0xFE0F),
    render: (size = 'md') => {
      const sz = size === 'sm' ? '1.25rem' : size === 'lg' ? '1.875rem' : '1.5rem'
      return <span style={{ fontSize: sz, filter: 'sepia(1) saturate(7) hue-rotate(16deg) brightness(1.08)' }}>{String.fromCodePoint(0x267B, 0xFE0F)}</span>
    }
  },
  {
    key: 'publicidad',
    label: 'Publicidad',
    emoji: String.fromCodePoint(0x1F4E3),
    render: (size = 'md') => {
      const sz = size === 'sm' ? '1.25rem' : size === 'lg' ? '1.875rem' : '1.5rem'
      return <span style={{ fontSize: sz }}>{String.fromCodePoint(0x1F4E3)}</span>
    }
  },
  {
    key: 'campana',
    label: 'Campaña',
    emoji: String.fromCodePoint(0x1F3AF),
    render: (size = 'md') => {
      const sz = size === 'sm' ? '1.25rem' : size === 'lg' ? '1.875rem' : '1.5rem'
      return <span style={{ fontSize: sz }}>{String.fromCodePoint(0x1F3AF)}</span>
    }
  },
]

// Mapas de acceso rapido (backwards-compat con ContactsTable)
export const FLAG_EMOJI = Object.fromEntries(FLAG_OPTIONS.map(o => [o.key, o.emoji]))
export const FLAG_LABEL = Object.fromEntries(FLAG_OPTIONS.map(o => [o.key, o.label]))
